import { useMemo, useState } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-hot-toast";
import ConfirmationModal from "../../../../components/Common/ConfirmationModal";
import TableSkeletonLoader from "../../../../components/Common/TableSkeletonLoader";
import {
  RequestStatusBadge,
  KycStatusBadge,
  ParticipantTypeBadge,
} from "../../../../components/Admin/InvestorPortal/AdminStatusBadge";
import InvestorPortalDetailHeader from "../../../../components/Admin/InvestorPortal/InvestorPortalDetailHeader";
import {
  DetailSection,
  DetailField,
  DetailFieldList,
} from "../../../../components/Admin/InvestorPortal/InvestorPortalDetailFields";
import InvestorPortalActionButton, {
  InvestorPortalActionStack,
} from "../../../../components/Admin/InvestorPortal/InvestorPortalActionButton";
import InvestorPortalTimeline from "../../../../components/Admin/InvestorPortal/InvestorPortalTimeline";
import { INVESTMENT_REQUEST_STATUS } from "../../../../store/admin/investorPortal/constants";
import { formatMoney } from "../../../../utils/currencyFormat";
import moment from "moment";
import { getRecommendedRequestAction } from "../../../../store/admin/investorPortal/requestLifecycle";
import {
  useGetStaffInvestmentRequestQuery,
  useStaffInvestmentRequestActionMutation,
} from "../../../../store/admin/investorPortal/staffInvestmentsApi";
import { mapStaffInvestmentRequestDetail } from "../../../../utils/staffInvestmentsAdapter";
import { withInvestorPortalGuard } from "../withInvestorPortalGuard";

function RecommendedActionCard({ status }) {
  const rec = getRecommendedRequestAction(status);
  return (
    <DetailSection title="Recommended next action">
      <p className="fw-semibold mb-1" style={{ color: "#383838", fontSize: 14 }}>
        {rec.label}
      </p>
      <p className="text-muted small mb-0">{rec.description}</p>
    </DetailSection>
  );
}

function InvestmentRequestDetailPage() {
  const { requestId } = useParams();
  const navigate = useNavigate();
  const [noteText, setNoteText] = useState("");
  const [declineModal, setDeclineModal] = useState(false);
  const [declineReason, setDeclineReason] = useState("");
  const [requestAction, { isLoading: isActionLoading }] = useStaffInvestmentRequestActionMutation();

  const { data: apiRequest, isFetching, isError } = useGetStaffInvestmentRequestQuery(requestId);

  const request = useMemo(() => mapStaffInvestmentRequestDetail(apiRequest), [apiRequest]);

  if (isFetching) {
    return (
      <div className="pb-4">
        <TableSkeletonLoader rows={8} columns={2} />
      </div>
    );
  }

  if (!request || isError) {
    return <Navigate to="/super_admin/investor_portal/investments/requests" replace />;
  }

  const status = request.status;
  const isPendingPayment = status === INVESTMENT_REQUEST_STATUS.PENDING_PAYMENT;
  const isSubmitted = status === INVESTMENT_REQUEST_STATUS.SUBMITTED;
  const isUnderReview = status === INVESTMENT_REQUEST_STATUS.UNDER_REVIEW;
  const isApproved = status === INVESTMENT_REQUEST_STATUS.APPROVED;
  const isAllocated = status === INVESTMENT_REQUEST_STATUS.ALLOCATED;
  const isTerminal = [
    INVESTMENT_REQUEST_STATUS.DECLINED,
    INVESTMENT_REQUEST_STATUS.ALLOCATED,
    INVESTMENT_REQUEST_STATUS.MATURED,
    INVESTMENT_REQUEST_STATUS.CANCELLED,
  ].includes(status);

  const act = async (nextStatus, msg, extra = {}) => {
    const actionMap = {
      [INVESTMENT_REQUEST_STATUS.UNDER_REVIEW]: "under-review",
      [INVESTMENT_REQUEST_STATUS.APPROVED]: "approve",
      [INVESTMENT_REQUEST_STATUS.DECLINED]: "decline",
      [INVESTMENT_REQUEST_STATUS.ALLOCATED]: "allocate",
      [INVESTMENT_REQUEST_STATUS.CANCELLED]: "cancel",
      [INVESTMENT_REQUEST_STATUS.MATURED]: "mark-matured",
    };
    const action = actionMap[nextStatus];
    if (!action) return;

    const body =
      action === "decline"
        ? { reason: extra.reason || declineReason }
        : action === "cancel"
          ? { reason: extra.reason || "Cancelled by staff" }
          : undefined;

    const actionKey = requestId || request.reference || request.id;

    try {
      await requestAction({ id: actionKey, action, body }).unwrap();
      toast.success(msg);
      setDeclineModal(false);
      setDeclineReason("");
    } catch (error) {
      toast.error(error?.data?.message || "Action failed.");
    }
  };

  const addNote = async (text) => {
    if (!text.trim()) return;
    const actionKey = requestId || request.reference || request.id;
    try {
      await requestAction({ id: actionKey, action: "notes", body: { note: text.trim() } }).unwrap();
      setNoteText("");
      toast.success("Note added");
    } catch (error) {
      toast.error(error?.data?.message || "Unable to add note.");
    }
  };

  const paymentLabel = request.isPaid
    ? "Paid"
    : isPendingPayment
      ? "Unpaid — awaiting Paystack"
      : "—";

  return (
    <div className="pb-4">
      <InvestorPortalDetailHeader
        title="Request"
        highlight={request.id}
        onBack={() => navigate("/super_admin/investor_portal/investments/requests")}
      />

      <div className="row g-3 investor-portal-detail-layout">
        <div className="col-lg-8">
          <DetailSection>
            <div className="investor-portal-status-row">
              <RequestStatusBadge status={request.status} />
              <KycStatusBadge status={request.kycStatus} />
            </div>

            <h5 className="fw-bold mb-1" style={{ color: "#383838" }}>
              {request.opportunityTitle}
            </h5>
            <p className="text-muted small mb-4">
              Submitted {moment(request.submittedAt).format("DD-MMM-YYYY HH:mm")}
            </p>

            <DetailFieldList>
              <DetailField label="Participant">
                <span className="d-inline-flex flex-wrap align-items-center gap-2">
                  <span>{request.participantName}</span>
                  <ParticipantTypeBadge type={request.participantType} />
                </span>
              </DetailField>
              <DetailField label="Email" value={request.participantEmail} />
              <DetailField label="Phone" value={request.participantPhone} />
              <DetailField label="Units" value={request.units} />
              <DetailField label="Principal">
                <span className="fw-bold">{formatMoney(request.totalNgn)}</span>
              </DetailField>
              <DetailField label="Payment status" value={paymentLabel} />
              {request.amountPaid != null && (
                <DetailField label="Amount paid">
                  <span className="fw-bold">{formatMoney(request.amountPaid)}</span>
                </DetailField>
              )}
              {request.paidAt && (
                <DetailField
                  label="Paid at"
                  value={moment(request.paidAt).format("DD-MMM-YYYY HH:mm")}
                />
              )}
              {request.paymentReference && (
                <DetailField label="Payment reference" value={request.paymentReference} />
              )}
              <DetailField label="Reviewed by" value={request.reviewedByName || "—"} />
            </DetailFieldList>
          </DetailSection>

          <DetailSection title="Timeline">
            <InvestorPortalTimeline items={request.timeline} />
          </DetailSection>

          <DetailSection title="Staff notes">
            {(request.staffNotes || []).length === 0 ? (
              <p className="text-muted small mb-3">No notes yet.</p>
            ) : (
              (request.staffNotes || []).map((n, i) => (
                <div key={i} className="investor-portal-note-card">
                  <p className="small mb-1">{n.text}</p>
                  <p className="text-muted small mb-0">
                    {n.by} · {moment(n.at).format("DD-MMM-YYYY")}
                  </p>
                </div>
              ))
            )}
            <div className="d-flex flex-column flex-sm-row gap-2 mt-2">
              <input
                className="form-control investor-portal-note-input flex-grow-1"
                placeholder="Add note..."
                value={noteText}
                onChange={(e) => setNoteText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && noteText.trim()) {
                    addNote(noteText.trim());
                  }
                }}
              />
              <InvestorPortalActionButton
                btnText="Add note"
                variant="primary"
                fullWidth={false}
                disabled={!noteText.trim() || isActionLoading}
                onClick={() => addNote(noteText)}
              />
            </div>
          </DetailSection>
        </div>

        <div className="col-lg-4">
          <RecommendedActionCard status={status} />
          <DetailSection title="Actions">
            <InvestorPortalActionStack>
              <InvestorPortalActionButton
                btnText="Mark under review"
                variant="ghost"
                disabled={!isSubmitted || isActionLoading}
                onClick={() => act(INVESTMENT_REQUEST_STATUS.UNDER_REVIEW, "Marked under review")}
              />
              <InvestorPortalActionButton
                btnText="Approve"
                variant="success"
                disabled={!(isSubmitted || isUnderReview) || isActionLoading}
                onClick={() => act(INVESTMENT_REQUEST_STATUS.APPROVED, "Request approved")}
              />
              <InvestorPortalActionButton
                btnText={isPendingPayment ? "Decline / release hold" : "Decline"}
                variant="danger"
                disabled={
                  isTerminal ||
                  isActionLoading ||
                  !(isPendingPayment || isSubmitted || isUnderReview || isApproved)
                }
                onClick={() => setDeclineModal(true)}
              />
              <InvestorPortalActionButton
                btnText="Allocate"
                variant="teal"
                disabled={!isApproved || isAllocated || request.isPaid || isActionLoading}
                onClick={() => act(INVESTMENT_REQUEST_STATUS.ALLOCATED, "Request allocated")}
              />
              {isAllocated && (
                <InvestorPortalActionButton
                  btnText="Mark matured"
                  variant="success"
                  disabled={isActionLoading}
                  onClick={() => act(INVESTMENT_REQUEST_STATUS.MATURED, "Marked matured")}
                />
              )}
              <InvestorPortalActionButton
                btnText="View opportunity"
                variant="outline"
                onClick={() =>
                  navigate(`/super_admin/investor_portal/investments/opportunities/${request.opportunityId}`)
                }
              />
            </InvestorPortalActionStack>
          </DetailSection>
        </div>
      </div>

      <ConfirmationModal
        isOpen={declineModal}
        onClose={() => setDeclineModal(false)}
        onConfirm={() => {
          if (!declineReason.trim()) {
            toast.error("Reason is required");
            return;
          }
          act(INVESTMENT_REQUEST_STATUS.DECLINED, "Request declined", {
            reason: declineReason.trim(),
          });
        }}
        title="Decline request"
        message={
          <div>
            <p className="mb-2">
              {isPendingPayment
                ? "Declining will release the soft-held units. Provide a reason."
                : "Provide a reason for declining this request."}
            </p>
            <textarea
              className="form-control investor-portal-note-input"
              rows={3}
              value={declineReason}
              onChange={(e) => setDeclineReason(e.target.value)}
            />
          </div>
        }
        confirmText="Decline"
        confirmButtonClass="bg-danger text-white"
        isLoading={isActionLoading}
      />
    </div>
  );
}

export default withInvestorPortalGuard(InvestmentRequestDetailPage);
